import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  orderBy,
  query,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import ReplyList from "./ReplyList";
import { useAuth } from "../contexts/AuthContext";
import styles from "../styles/PostFeed.module.css";

const CLOUDINARY_UPLOAD_PRESET = "unsigned_preset";
const CLOUDINARY_CLOUD_NAME = "dioeq3s5r";

// Cloudinary upload helper (Same logic)
const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) throw new Error("Cloudinary upload failed");

  const data = await response.json();
  return {
    url: data.secure_url,
    type: "image",
    name: data.public_id,
  };
};

export default function PostFeed() {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState({});
  const { currentUser: user } = useAuth();
  const [userMap, setUserMap] = useState({});
  const [tab, setTab] = useState("trending");
  const [following, setFollowing] = useState([]);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [editMode, setEditMode] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.email || !user?.uid) return;

    const unsubscribePosts = onSnapshot(
      query(collection(db, "posts"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const postData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPosts(postData);
        setLoading(false);
      }
    );

    const unsubscribeFollow = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
      const data = docSnap.data();
      setFollowing(data?.following || []);
    });

    return () => {
      unsubscribePosts();
      unsubscribeFollow();
    };
  }, [user]);

  // Optimize: Fetch user details only for the posts we have
  useEffect(() => {
    const fetchAuthors = async () => {
      const uniqueAuthorIds = [...new Set(
        posts
          .filter(p => p.authorUid && !userMap[p.author])
          .map(p => p.authorUid)
      )];

      if (uniqueAuthorIds.length === 0) return;

      const newUsers = {};
      // Fetch in batches or individually. For simplicity and since we have IDs, we can use getDoc.
      // Ideally use a where 'in' query for batches, but simple getDoc is fine for now compared to loading ALL users.

      await Promise.all(uniqueAuthorIds.map(async (uid) => {
        try {
          const userDoc = await import("firebase/firestore").then(pkg => pkg.getDoc(pkg.doc(db, "users", uid)));
          if (userDoc.exists()) {
            const data = userDoc.data();
            // Map by email as that's how the render logic currently looks up: userMap[post.author]
            if (data.email) newUsers[data.email] = data.username || data.displayName;
          }
        } catch (e) {
          console.warn("Failed to fetch author", uid, e);
        }
      }));

      if (Object.keys(newUsers).length > 0) {
        setUserMap(prev => ({ ...prev, ...newUsers }));
      }
    };

    if (posts.length > 0) {
      fetchAuthors();
    }
  }, [posts, userMap]);

  const handlePost = async () => {
    if (!user?.email) return alert("User not found.");
    if (!newPost.trim() && files.length === 0 && existingFiles.length === 0)
      return alert("Post cannot be empty.");

    try {
      const uploadedFiles = [];
      for (const file of files) {
        const uploaded = await uploadToCloudinary(file);
        uploadedFiles.push(uploaded);
      }

      const finalFiles = [...existingFiles, ...uploadedFiles];

      if (editMode) {
        await updateDoc(doc(db, "posts", editMode), {
          text: newPost,
          files: finalFiles,
        });
        setEditMode(null);
      } else {
        await addDoc(collection(db, "posts"), {
          text: newPost,
          files: finalFiles,
          author: user.email,
          authorUid: user.uid,
          createdAt: serverTimestamp(),
          likes: [],
          reports: [],
        });
      }

      setNewPost("");
      setFiles([]);
      setExistingFiles([]);
    } catch (err) {
      alert("Error posting: " + err.message);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deleteDoc(doc(db, "posts", postId));
    } catch (err) {
      alert("Error deleting post: " + err.message);
    }
  };

  const handleEdit = (post) => {
    setNewPost(post.text);
    setFiles([]);
    setExistingFiles(post.files || []);
    setEditMode(post.id);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const newFiles = [...files];
    let imageCount = newFiles.length + existingFiles.length;

    for (const file of selected) {
      if (imageCount >= 5) {
        alert("Only up to 5 images allowed.");
        break;
      }
      imageCount++;
      newFiles.push(file);
    }

    setFiles(newFiles.slice(0, 5 - existingFiles.length));
  };

  const removeExistingImage = (index) => {
    const updated = [...existingFiles];
    updated.splice(index, 1);
    setExistingFiles(updated);
  };

  const toggleLike = async (postId, likes = []) => {
    if (!user) return;
    const ref = doc(db, "posts", postId);
    const hasLiked = likes.includes(user.uid);
    try {
      await updateDoc(ref, {
        likes: hasLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      });
    } catch (err) {
      alert("Error updating like: " + err.message);
    }
  };

  const reportPost = async (postId, reports = []) => {
    if (!user || reports.includes(user.uid)) return;
    try {
      await updateDoc(doc(db, "posts", postId), {
        reports: arrayUnion(user.uid),
      });
      alert("Post reported.");
    } catch (err) {
      alert("Error reporting post: " + err.message);
    }
  };

  const handleReply = async (postId) => {
    const reply = replyText[postId]?.trim();
    if (!reply || !user) return;
    try {
      await addDoc(collection(db, "posts", postId, "replies"), {
        text: reply,
        author: user.email,
        createdAt: serverTimestamp(),
      });
      setReplyText((prev) => ({ ...prev, [postId]: "" }));
    } catch (err) {
      alert("Error replying: " + err.message);
    }
  };

  const toggleComments = (postId) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  const filteredPosts =
    tab === "following"
      ? posts.filter((post) => following.includes(post.authorUid))
      : posts;

  return (
    <div className={styles.feedContainer}>
      <div className={styles.createPost}>
        <div className={styles.heading}>{editMode ? "Edit Post" : "Create a Post"}</div>
        <textarea
          className={styles.textarea}
          rows="3"
          value={newPost}
          onChange={(e) => setNewPost(e.target.value)}
          placeholder="What's on your mind?"
        />

        {(files.length > 0 || existingFiles.length > 0) && (
          <div className={styles.previewContainer}>
            {existingFiles.map((file, index) => (
              <div key={`exist-${index}`} className={styles.previewItem}>
                <img src={file.url} alt="existing" className={styles.previewImage} />
                <button onClick={() => removeExistingImage(index)} className={styles.removeBtn}>✕</button>
              </div>
            ))}
            {files.map((file, index) => {
              const url = URL.createObjectURL(file);
              return (
                <div key={`new-${index}`} className={styles.previewItem}>
                  <img src={url} alt="new" className={styles.previewImage} />
                  <button
                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    className={styles.removeBtn}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.actions}>
          <label className={styles.uploadLabel}>
            📷 Add Photos
            <input type="file" multiple accept="image/*" onChange={handleFileSelect} className={styles.fileInput} />
          </label>

          <div>
            {editMode && (
              <button
                onClick={() => {
                  setEditMode(null);
                  setNewPost("");
                  setFiles([]);
                  setExistingFiles([]);
                }}
                className="btn-ghost"
                style={{ marginRight: '10px' }}
              >
                Cancel
              </button>
            )}
            <button onClick={handlePost} className="btn-primary">
              {editMode ? "Update" : "Post"}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === "trending" ? styles.activeTab : ""}`}
          onClick={() => setTab("trending")}
        >
          🔥 Trending
        </button>
        <button
          className={`${styles.tab} ${tab === "following" ? styles.activeTab : ""}`}
          onClick={() => setTab("following")}
        >
          👥 Following
        </button>
      </div>

      {loading ? (
        <p>Loading feeds...</p>
      ) : filteredPosts.length === 0 ? (
        <p>No posts yet. Be the first to post!</p>
      ) : (
        filteredPosts.map((post) => (
          <div key={post.id} className={styles.post}>
            <div className={styles.postHeader}>
              <span
                className={styles.author}
                onClick={() => navigate(`/profile/${encodeURIComponent(post.author)}`)}
              >
                @{userMap[post.author] || post.author.split('@')[0]}
              </span>
              <span className={styles.timestamp}>
                {post.createdAt?.toDate?.().toLocaleString() || "Just now"}
              </span>
            </div>

            <div className={styles.postContent}>{post.text}</div>

            {post.files?.length > 0 && (
              <div className={styles.postImages} style={{ gridTemplateColumns: post.files.length > 1 ? '1fr 1fr' : '1fr' }}>
                {post.files.map((file, i) => (
                  <img key={i} src={file.url} alt="post" className={styles.postImage} />
                ))}
              </div>
            )}

            <div className={styles.postActions}>
              <button onClick={() => toggleLike(post.id, post.likes)} className={styles.actionBtn}>
                {post.likes?.includes(user?.uid) ? "❤️" : "🤍"} {post.likes?.length || 0}
              </button>
              <button onClick={() => toggleComments(post.id)} className={styles.actionBtn}>
                💬 {clicked => "Comment"}
              </button>
              <button onClick={() => reportPost(post.id, post.reports)} className={styles.actionBtn}>
                🚩 Report
              </button>

              {post.authorUid === user?.uid && (
                <>
                  <button onClick={() => handleEdit(post)} className={styles.actionBtn} style={{ marginLeft: "auto" }}>✏️</button>
                  <button onClick={() => handleDelete(post.id)} className={`${styles.actionBtn} ${styles.deleteBtn}`}>🗑️</button>
                </>
              )}
            </div>

            {expandedPosts[post.id] && (
              <div className={styles.replySection}>
                <textarea
                  className={styles.replyInput}
                  rows="2"
                  placeholder="Write a reply..."
                  value={replyText[post.id] || ""}
                  onChange={(e) =>
                    setReplyText((prev) => ({ ...prev, [post.id]: e.target.value }))
                  }
                />
                <button onClick={() => handleReply(post.id)} className="btn-primary" style={{ fontSize: '0.8rem', padding: '4px 12px' }}>
                  Reply
                </button>
                <ReplyList postId={post.id} />
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}